import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import AlertMessage from '../../components/ui/AlertMessage';
import DynamicTable from '../../components/Table/DynamicTable';
import Pagination from '../../components/Pagination/Pagination';
import SearchIcon from '../../components/icons/SearchIcon';
import EyeIcon from '../../components/icons/EyeIcon';
import TrashFillIcon from '../../components/icons/TrashFillIcon';
import AddIcon from '../../components/icons/AddIcon';
import { useAuth } from '../../contexts/AuthContext';

// Define the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const EvaluationListAdminScreen = () => {
    const navigate = useNavigate(); // Initialize useNavigate hook
    const { token } = useAuth(); // Get the authentication token
    const [evaluations, setEvaluations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; // Number of items per page

    // Options for the evaluation type filter
    const evaluationTypeOptions = [
        { value: "", label: "Filtrar por Tipo de Evaluación" },
        { value: "Sello Checkin", label: "Sello Checkin" },
        { value: "Auditoría Anual", label: "Auditoría Anual" },
        // Add more types as needed
    ];

    useEffect(() => {
        const fetchAndEnrichEvaluations = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // 1. Fetch the list of evaluations
                // NOTE: We assume the endpoint for listing evaluations is `/evaluations`.
                const evaluationsResponse = await fetch(`${API_BASE_URL}/api/supplier-profiles`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!evaluationsResponse.ok) throw new Error('No se pudieron obtener las evaluaciones.');
                const evaluationsData = await evaluationsResponse.json();

                if (evaluationsData.length === 0) {
                    setEvaluations([]);
                    return;
                }

                // 2. Get all unique supplier IDs to avoid redundant fetches
                const supplierIds = [...new Set(evaluationsData.map(e => e.supplier_id).filter(id => id))];

                // 3. Fetch all required suppliers in parallel
                // NOTE: We assume the endpoint for a single supplier is `/suppliers/:id`.
                const supplierPromises = supplierIds.map(id =>
                    fetch(`${API_BASE_URL}/api/suppliers/${id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }).then(res => res.ok ? res.json() : Promise.reject(`No se pudo obtener el proveedor con ID ${id}`))
                );
                const suppliersData = await Promise.all(supplierPromises);

                // 4. Create a map for quick lookup of supplier names
                const supplierMap = new Map(suppliersData.map(s => [s.id, s.name]));

                // 5. Enrich evaluations with supplier names and other required fields
                const getStatusClass = (status) => {
                    switch (status) {
                        case 'Aprobada': return 'bg-success';
                        case 'En Revisión': return 'bg-warning text-dark';
                        case 'Rechazada': return 'bg-danger';
                        case 'Pendiente': return 'bg-secondary';
                        default: return 'bg-light text-dark';
                    }
                };

                const enrichedEvaluations = evaluationsData.map(evaluation => ({
                    id: evaluation.id,
                    tid: `EVAL-${String(evaluation.id).padStart(6, '0')}`,
                    providerName: supplierMap.get(evaluation.supplier_id) || 'Nombre no disponible',
                    creationDate: evaluation.created_at ? new Date(evaluation.created_at).toLocaleDateString() : 'N/A',
                    type: evaluation.type,
                    score: evaluation.score >= 0 ? `${evaluation.score}/100` : 'N/A',
                    status: evaluation.status,
                    statusClass: getStatusClass(evaluation.status),
                }));

                setEvaluations(enrichedEvaluations);

            } catch (err) {
                setError(err.message);
                console.error("Error fetching data:", err);
            } finally {
                setIsLoading(false);
            }
        };

        if (token) {
            fetchAndEnrichEvaluations();
        }
    }, [token]);

    // Define columns for the DynamicTable
    const evaluationColumns = [
        { header: 'ID Evaluación', accessor: 'tid' },
        { header: 'Nombre Proveedor', accessor: 'providerName' },
        { header: 'Fecha de Creación', accessor: 'creationDate' },
        { header: 'Tipo Evaluación', accessor: 'type' },
        { header: 'Puntaje', accessor: 'score' },
        {
            header: 'Estado',
            render: (evaluation) => (
                <span className={`badge rounded-pill ${evaluation.statusClass}`}>
                    {evaluation.status}
                </span>
            )
        },
    ];

    // Define actions for the DynamicTable
    const evaluationActions = [
        {
            icon: <EyeIcon />,
            tooltip: 'Ver Detalle Evaluación',
            className: 'btn-outline-info-checkin',
            onClick: (evaluation) => navigate(`/evaluations/${evaluation.id}`) // Navigate to the new detail screen
        },
        {
            icon: <TrashFillIcon />,
            tooltip: 'Eliminar Evaluación',
            className: 'btn-outline-danger-checkin',
            onClick: (evaluation) => console.log('Delete evaluation:', evaluation.id) // Replace with actual deletion logic
        },
    ];

    // Define which columns are searchable
    const searchableColumns = ['id', 'providerName', 'creationDate', 'type', 'score', 'status'];

    // Apply search and filter logic
    const filteredEvaluations = evaluations.filter(evaluation => {
        const matchesSearchTerm = searchTerm.trim() === '' ||
            searchableColumns.some(column => {
                const value = String(evaluation[column]).toLowerCase();
                return value.includes(searchTerm.toLowerCase());
            });

        const matchesFilterType = filterType === '' || evaluation.type === filterType;

        return matchesSearchTerm && matchesFilterType;
    });

    const totalItems = filteredEvaluations.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    // Calculate the start and end index for the current page
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedEvaluations = filteredEvaluations.slice(startIndex, endIndex);

    // Reset current page when search term or filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterType]);

    return (
        <>
            <FormSpinnerOverlay isLoading={isLoading} />
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <h1 className="h2 mb-md-0 mb-4">Gestión de Evaluaciones</h1>
            </div>

            <div className="row text-start mb-4">
                <p className="mb-2 text-muted">Listado y administración de las evaluaciones de los proveedores.</p>
                <div className="col-12">
                    <div className="row g-3 mb-3 align-items-center d-flex justify-content-between">
                        <div className="col-md-8">
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Buscar evaluaciones..."
                                    aria-label="Buscar evaluaciones"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <button className="btn btn-outline-secondary" type="button">
                                    <SearchIcon />
                                </button>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <select
                                className="form-select"
                                id="filtroTipoEvaluacion"
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                {evaluationTypeOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="card card-metric border-0">
                        <div className="card-body px-md-2 px-0">
                            {isLoading && <FormSpinnerOverlay isLoading={isLoading} />}
                            {error && <AlertMessage type="danger" message={`Error al cargar evaluaciones: ${error}`} />}
                            {!isLoading && !error && filteredEvaluations.length === 0 && (
                                <AlertMessage type="info" message="No hay evaluaciones registradas que coincidan con los criterios." />
                            )}

                            {!isLoading && !error && filteredEvaluations.length > 0 && (
                                <DynamicTable
                                    data={paginatedEvaluations}
                                    columns={evaluationColumns}
                                    actions={evaluationActions}
                                    keyAccessor="id"
                                />
                            )}

                            {totalPages > 1 && (
                                <Pagination
                                    currentPage={currentPage}
                                    itemsPerPage={itemsPerPage}
                                    totalItems={totalItems}
                                    onPageChange={(page) => setCurrentPage(page)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {/* The copyright section from the HTML. In a real app, this might be part of a global footer. */}
            <div className="copyright mt-auto text-center">
                <p>© 2025 Checkin. Todos los derechos reservados. Desarrollado por <a href="https://antudigital.cl" target="_blank" rel="noopener noreferrer">Antü Digital</a></p>
            </div>
        </>
    );
};

export default EvaluationListAdminScreen;